import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  CreateCampaignRulePayload,
  UpdateCampaignRulePayload,
  createCampaignRule,
  deleteCampaignRule,
  listCampaignRules,
  updateCampaignRule,
} from '~/services/AccommodationService';

export const campaignRuleQueryKeys = {
  all: ['campaignRules'] as const,
  lists: () => [...campaignRuleQueryKeys.all, 'list'] as const,
  list: (campaignId: string) => [...campaignRuleQueryKeys.lists(), campaignId] as const,
};

export function useGetCampaignRules({ campaignId, enabled = true }: { campaignId: string; enabled?: boolean }) {
  return useQuery({
    queryKey: campaignRuleQueryKeys.list(campaignId),
    queryFn: async ({ signal }) => {
      const response = await listCampaignRules({ campaignId, signal });
      return response.result.rules;
    },
    enabled: enabled && !!campaignId,
  });
}

export function useCreateCampaignRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, payload }: { campaignId: string; payload: CreateCampaignRulePayload }) => {
      const response = await createCampaignRule({ campaignId, payload });
      return response.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignRuleQueryKeys.list(variables.campaignId) });
      enqueueSnackbar('Rule created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create rule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateCampaignRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      campaignId,
      ruleId,
      payload,
    }: {
      campaignId: string;
      ruleId: string;
      payload: UpdateCampaignRulePayload;
    }) => {
      return updateCampaignRule({ campaignId, ruleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignRuleQueryKeys.list(variables.campaignId) });
      enqueueSnackbar('Rule updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update rule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useDeleteCampaignRule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ campaignId, ruleId }: { campaignId: string; ruleId: string }) => {
      return deleteCampaignRule({ campaignId, ruleId });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignRuleQueryKeys.list(variables.campaignId) });
      enqueueSnackbar('Rule deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete rule: ${error.message}`, { variant: 'error' });
    },
  });
}
